import { Dialog, Transition } from "@headlessui/react";
import { Fragment, JSX, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  ChevronDown,
  ChevronRight,
  CircleX,
  Download,
  Eye,
  FileText,
  Folder,
  Info,
  Loader2,
  Paperclip,
  RefreshCw,
  X,
} from "lucide-react";
import { useGetAllCompanyFiles } from "../../queries/onboarding";
import { FileTree } from "../FileExplorer/FileExplorer";
import Loader from "../../utils/loader";
import {
  extractFileDate,
  formatExtractedDate,
  toISODate,
  ExtractedDate,
} from "../../utils/extractFileDate";
import {
  CaptableEntry,
  DownloadEntry,
  FinancialEntry,
  isCaptableRunInFlight,
  isFinancialRunInFlight,
  isJobInFlight,
  makeRequestKey,
  useCaptableStore,
  useDownloadsStore,
  useFinancialStore,
} from "../../store";
import {
  CaptableExtractionStats,
  CaptableRunSummary,
  PdfAttachment,
  extractPdfAttachments,
  getMcaFileSignedUrl,
  isCaptableRunCancelled,
} from "../../api/dataPipeline";
import { useRerunMgt7, useRerunPas3 } from "../../queries/dataPipeline";
import { PdfViewerModal } from "./PdfViewerModal";
import {
  FileTypeKey,
  FORM_TYPE_LABELS,
  classifyFile,
  dedupeByFilingDate,
  flattenFileTree,
} from "./fileSelection";

export type { FileTypeKey } from "./fileSelection";
type ActiveTab = FileTypeKey | "SELECTED";

const FILE_TYPE_TABS: FileTypeKey[] = [
  "ALL",
  "PAS-3",
  "MGT-7",
  "MGT-14",
  "AOC-4",
  "MOA",
  "SH-7",
  "SH-8",
  "SH-9",
  "SH-11",
];

type Props = {
  cin: string;
  companyName?: string | null;
  showPopup: boolean;
  onClose: () => void;
  latestCaptableRun?: CaptableRunSummary | null;
};

type DupAnnotation = {
  path: string;
  label: string;
  tone: "info" | "warn";
};

type Pas3RunFileStatus = {
  inLastRun: boolean;
  issues: string[];
};

type Pas3RunLookup = {
  runFull: Set<string>;
  runBase: Set<string>;
  issueByFull: Map<string, string[]>;
  issueByBase: Map<string, string[]>;
};

function baseName(path: string): string {
  return path.split("/").pop() ?? path;
}

function compactSearchText(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function matchesFilenameSearch(
  path: string,
  term: string,
  compactTerm: string,
): boolean {
  if (!term) return true;
  const fileName = baseName(path).toLowerCase();
  const rawSubstringMatch = fileName.includes(term);
  const compactSubstringMatch =
    compactTerm.length > 0 && compactSearchText(fileName).includes(compactTerm);
  return rawSubstringMatch || compactSubstringMatch;
}

function isIsoDateWithinRange(
  isoDate: string,
  fromDate: string,
  toDate: string,
): boolean {
  if (fromDate && isoDate < fromDate) return false;
  if (toDate && isoDate > toDate) return false;
  return true;
}

function stripStatsIndexPrefix(fileName: string): string {
  // Stats may prefix filenames with an ordering index like "0-<name>.pdf".
  // Matching should be based on the original MCA filename.
  return fileName.replace(/^\d+-/, "");
}

function normalizePas3Path(path: string, cin?: string): string {
  const trimmed = path.replace(/^\/+/, "");
  if (!trimmed) return "";

  let withoutCin = trimmed;
  if (
    cin &&
    trimmed.toLowerCase().startsWith(`${cin.toLowerCase()}/`) &&
    trimmed.length > cin.length + 1
  ) {
    withoutCin = trimmed.slice(cin.length + 1);
  }
  const parts = withoutCin.split("/");
  const name = parts.pop() ?? "";
  const canonicalName = stripStatsIndexPrefix(name);
  const canonicalPath =
    parts.length > 0 ? `${parts.join("/")}/${canonicalName}` : canonicalName;
  return canonicalPath.toLowerCase();
}

function pushUnique(items: string[], value: string) {
  if (!items.includes(value)) items.push(value);
}

function addIssue(
  map: Map<string, string[]>,
  key: string,
  message: string,
): void {
  if (!key) return;
  const lines = map.get(key);
  if (lines) {
    pushUnique(lines, message);
    return;
  }
  map.set(key, [message]);
}

function buildPas3RunLookup(
  run: CaptableRunSummary | null | undefined,
  cin: string,
): Pas3RunLookup {
  const lookup: Pas3RunLookup = {
    runFull: new Set<string>(),
    runBase: new Set<string>(),
    issueByFull: new Map<string, string[]>(),
    issueByBase: new Map<string, string[]>(),
  };
  if (!run) return lookup;

  const addRunPath = (
    rawPath: string,
    fullSet: Set<string>,
    baseSet: Set<string>,
  ) => {
    const full = normalizePas3Path(rawPath, cin);
    if (!full) return;
    fullSet.add(full);
    baseSet.add(baseName(full));
  };

  run.pas3Keys.forEach((key) =>
    addRunPath(key, lookup.runFull, lookup.runBase),
  );

  const stats = run.extractionStats;
  if (!stats) return lookup;

  const extractedFull = new Set<string>();
  const extractedBase = new Set<string>();

  stats.pas3s.forEach((p) => {
    const full = normalizePas3Path(p.file, cin);
    const base = baseName(full);
    addRunPath(p.file, lookup.runFull, lookup.runBase);
    addRunPath(p.file, extractedFull, extractedBase);

    if (p.rounds === 0) {
      addIssue(lookup.issueByFull, full, "0 rounds extracted");
      addIssue(lookup.issueByBase, base, "0 rounds extracted");
    }
    if (p.placeholders > 0) {
      const msg = `${p.placeholders} placeholder allottee${
        p.placeholders === 1 ? "" : "s"
      }`;
      addIssue(lookup.issueByFull, full, msg);
      addIssue(lookup.issueByBase, base, msg);
    }
    p.loaIssues.forEach((issue) => {
      addIssue(lookup.issueByFull, full, issue);
      addIssue(lookup.issueByBase, base, issue);
    });
  });

  run.pas3Keys.forEach((key) => {
    const full = normalizePas3Path(key, cin);
    const base = baseName(full);
    if (!extractedFull.has(full) && !extractedBase.has(base)) {
      addIssue(
        lookup.issueByFull,
        full,
        "No extraction stats (possible failure)",
      );
      addIssue(
        lookup.issueByBase,
        base,
        "No extraction stats (possible failure)",
      );
    }
  });

  return lookup;
}

function getPas3StatusForPath(
  path: string,
  lookup: Pas3RunLookup,
): Pas3RunFileStatus {
  const full = normalizePas3Path(path);
  const base = baseName(full);
  const mergedIssues: string[] = [];

  (lookup.issueByFull.get(full) ?? []).forEach((line) =>
    pushUnique(mergedIssues, line),
  );
  (lookup.issueByBase.get(base) ?? []).forEach((line) =>
    pushUnique(mergedIssues, line),
  );

  return {
    inLastRun: lookup.runFull.has(full) || lookup.runBase.has(base),
    issues: mergedIssues,
  };
}

function renderPas3StatusBadge(status: Pas3RunFileStatus): JSX.Element {
  if (status.issues.length > 0) {
    return (
      <span
        className="px-2 py-0.5 text-[10px] rounded border bg-red-50 text-red-700 border-red-200"
        title={status.issues.join("\n")}
      >
        Issue ({status.issues.length})
      </span>
    );
  }
  if (status.inLastRun) {
    return (
      <span className="px-2 py-0.5 text-[10px] rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
        Last run OK
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 text-[10px] rounded border bg-gray-100 text-gray-500 border-gray-200">
      Not in last run
    </span>
  );
}

function pruneTree(
  tree: FileTree | undefined,
  keep: (path: string) => boolean,
): FileTree | null {
  if (!tree) return null;
  const out: FileTree = {};
  for (const [key, value] of Object.entries(tree)) {
    if (value === "file") {
      if (keep(key)) out[key] = "file";
    } else if (typeof value === "object") {
      const sub = pruneTree(value, keep);
      if (sub && Object.keys(sub).length > 0) out[key] = sub;
    }
  }
  return out;
}

function collectAllFiles(tree: FileTree | undefined): string[] {
  if (!tree) return [];
  return Object.entries(tree).flatMap(([key, value]) => {
    if (value === "file") return [key];
    if (typeof value === "object") return collectAllFiles(value);
    return [];
  });
}

export const DataPipelineFilesPopup = ({
  cin,
  companyName,
  showPopup,
  onClose,
  latestCaptableRun,
}: Props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activeTab, setActiveTab] = useState<ActiveTab>("ALL");
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [viewingFiles, setViewingFiles] = useState<Set<string>>(new Set());

  type PdfEntry = {
    url?: string;
    base64Data?: { data: string; mimeType: string };
    s3Key?: string; // present for lazy URL fetch on back-navigation
    fileName: string;
  };
  const [pdfViewer, setPdfViewer] = useState<PdfEntry | null>(null);
  const [pdfViewerHistory, setPdfViewerHistory] = useState<PdfEntry[]>([]);
  const [navigatingBack, setNavigatingBack] = useState(false);
  const [attachmentsState, setAttachmentsState] = useState<{
    file: string;
    loading: boolean;
    attachments: PdfAttachment[];
    error: string | null;
  } | null>(null);

  const { data: companyFiles, isFetched } = useGetAllCompanyFiles(cin);

  const tree = companyFiles as FileTree | undefined;

  const files = useMemo(() => flattenFileTree(tree), [tree]);

  // Date extraction runs over every file in the tree (not just visible) so the
  // MGT-7 / PAS-3 pairing logic still works when the user is filtered to a
  // single form tab.
  const fileDates = useMemo(() => {
    const map = new Map<string, ExtractedDate | null>();
    for (const f of files) map.set(f, extractFileDate(f));
    return map;
  }, [files]);

  const hasDateRange = !!startDate || !!endDate;

  const visibleTree = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const compactTerm = compactSearchText(term);
    return (
      pruneTree(tree, (path) => {
        // Search should match the filename users see, not the full tree path.
        if (!matchesFilenameSearch(path, term, compactTerm)) return false;
        if (activeTab !== "ALL" && classifyFile(path) !== activeTab)
          return false;
        if (!hasDateRange) return true;
        const extracted = fileDates.get(path) ?? null;
        const isoDate = toISODate(extracted);
        if (!isoDate) return false;
        return isIsoDateWithinRange(isoDate, startDate, endDate);
      }) ?? {}
    );
  }, [
    tree,
    searchTerm,
    activeTab,
    hasDateRange,
    fileDates,
    startDate,
    endDate,
  ]);

  const undatedExcludedCount = useMemo(() => {
    if (!hasDateRange) return 0;
    const term = searchTerm.trim().toLowerCase();
    const compactTerm = compactSearchText(term);
    let count = 0;
    files.forEach((path) => {
      if (!matchesFilenameSearch(path, term, compactTerm)) return;
      if (activeTab !== "ALL" && classifyFile(path) !== activeTab) return;
      if (!fileDates.get(path)) count += 1;
    });
    return count;
  }, [hasDateRange, files, searchTerm, activeTab, fileDates]);

  const visibleFiles = useMemo(
    () => collectAllFiles(visibleTree),
    [visibleTree],
  );
  const visibleListKey = `${activeTab}:${searchTerm.trim()}:${startDate}:${endDate}`;

  // Most recent file per classifiable form type — used for the "Latest" badge
  // and the Pick-latest button. Operates over the full file list.
  const latestByType = useMemo(() => {
    const best = new Map<FileTypeKey, { path: string; ts: number }>();
    files.forEach((path) => {
      const type = classifyFile(path);
      const ts = fileDates.get(path)?.date.getTime();
      if (!type || ts === undefined) return;
      const prev = best.get(type);
      if (!prev || ts > prev.ts) best.set(type, { path, ts });
    });
    const result = new Map<FileTypeKey, string>();
    best.forEach((v, k) => result.set(k, v.path));
    return result;
  }, [files, fileDates]);

  // Cap-table workflow: anchor on the latest MGT-7 (annual snapshot of the
  // share register) plus every PAS-3 allotment, deduplicated to one filing per
  // date (highest leading sequence number wins on duplicates).
  const mgt7Pas3Selection = useMemo(() => {
    const mgt7Path = latestByType.get("MGT-7");
    const pas3Files = files.filter((f) => classifyFile(f) === "PAS-3");
    const { keptPaths, superseded } = dedupeByFilingDate(pas3Files, fileDates);
    return { mgt7Path, pas3Paths: keptPaths, superseded };
  }, [files, fileDates, latestByType]);

  // AOC-4 (financial filing) deduplication mirrors PAS-3: one filing per date,
  // keeping the highest-numbered copy and flagging the rest as superseded.
  const aoc4Selection = useMemo(() => {
    const aoc4Files = files.filter((f) => classifyFile(f) === "AOC-4");
    return dedupeByFilingDate(aoc4Files, fileDates);
  }, [files, fileDates]);

  // Flat sorted list used when a specific form-type tab is active — sorting by
  // date inside a tree view doesn't really work, so we collapse to a list.
  const sortedVisibleFiles = useMemo(() => {
    return [...visibleFiles].sort((a, b) => {
      const ta = fileDates.get(a)?.date.getTime();
      const tb = fileDates.get(b)?.date.getTime();
      if (ta === undefined && tb === undefined) return a.localeCompare(b);
      if (ta === undefined) return 1;
      if (tb === undefined) return -1;
      return tb - ta;
    });
  }, [visibleFiles, fileDates]);

  useEffect(() => {
    if (!visibleTree) return;
    // Auto-expand folders when filtering so matches are immediately visible
    if (searchTerm || activeTab !== "ALL" || hasDateRange) {
      const expandAll = (node: FileTree | undefined, acc: Set<string>) => {
        if (!node) return;
        for (const [key, value] of Object.entries(node)) {
          if (typeof value === "object") {
            acc.add(key);
            expandAll(value, acc);
          }
        }
      };
      const next = new Set<string>();
      expandAll(visibleTree, next);
      setExpanded(next);
    }
  }, [visibleTree, searchTerm, activeTab, hasDateRange]);

  const allVisibleSelected =
    visibleFiles.length > 0 && visibleFiles.every((f) => selectedFiles.has(f));

  const toggleFile = (file: string) => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(file)) next.delete(file);
      else next.add(file);
      return next;
    });
  };

  const toggleFolder = (path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const toggleFolderSelection = (
    folderTree: FileTree,
    _folderPath: string,
    select: boolean,
  ) => {
    const descendants = collectAllFiles(folderTree);
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (select) descendants.forEach((f) => next.add(f));
      else descendants.forEach((f) => next.delete(f));
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleFiles.forEach((f) => next.delete(f));
      } else {
        visibleFiles.forEach((f) => next.add(f));
      }
      return next;
    });
  };

  const handleTabChange = (tab: ActiveTab) => {
    setActiveTab(tab);
    // Exit attachment-only mode so the newly selected tab list is shown.
    setAttachmentsState(null);
  };

  const pickLatest = () => {
    if (latestByType.size === 0) return;
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (activeTab === "ALL" || activeTab === "SELECTED") {
        latestByType.forEach((path) => next.add(path));
      } else {
        const p = latestByType.get(activeTab);
        if (p) next.add(p);
      }
      return next;
    });
  };

  const pickPas3WithIssues = () => {
    if (pas3IssuePaths.length === 0) return;
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      pas3IssuePaths.forEach((path) => next.add(path));
      return next;
    });
  };

  const pickMgt7AndPas3 = () => {
    const { mgt7Path, pas3Paths } = mgt7Pas3Selection;
    if (!mgt7Path && pas3Paths.length === 0) return;
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (mgt7Path) next.add(mgt7Path);
      pas3Paths.forEach((p) => next.add(p));
      return next;
    });
  };

  const pairAvailable = !!(
    mgt7Pas3Selection.mgt7Path || mgt7Pas3Selection.pas3Paths.length
  );

  // Select every AOC-4, deduplicated to one filing per date (mirrors "all
  // PAS-3" in pickMgt7AndPas3 — the flagged duplicate copies are left out).
  const pickAllAoc4 = () => {
    const { keptPaths } = aoc4Selection;
    if (keptPaths.length === 0) return;
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      keptPaths.forEach((p) => next.add(p));
      return next;
    });
  };

  const aoc4Available = aoc4Selection.keptPaths.length > 0;

  // Per-file badges for PAS-3 / AOC-4 files that share a filing date: the kept
  // copy is flagged "info", the superseded duplicates "warn".
  const dupMarks = useMemo(() => {
    const marks = new Map<string, DupAnnotation>();
    const addMarks = (superseded: Map<string, string>) => {
      const winners = new Set<string>();
      superseded.forEach((winner, path) => {
        winners.add(winner);
        marks.set(path, { path, label: "Dup date — skipped", tone: "warn" });
      });
      winners.forEach((path) => {
        marks.set(path, { path, label: "Kept for date", tone: "info" });
      });
    };
    addMarks(mgt7Pas3Selection.superseded);
    addMarks(aoc4Selection.superseded);
    return marks;
  }, [mgt7Pas3Selection, aoc4Selection]);

  // Grouped, date-sorted view of the current selection so the user can confirm
  // exactly which MGT-7 / PAS-3 (etc.) are queued without hunting through the
  // tree. Unclassifiable files fall under "Other".
  const selectedSummary = useMemo(() => {
    const groups = new Map<FileTypeKey | "Other", string[]>();
    selectedFiles.forEach((path) => {
      const type = classifyFile(path) ?? "Other";
      const arr = groups.get(type) ?? [];
      arr.push(path);
      groups.set(type, arr);
    });
    groups.forEach((arr) =>
      arr.sort((a, b) => {
        const ta = fileDates.get(a)?.date.getTime();
        const tb = fileDates.get(b)?.date.getTime();
        if (ta === undefined && tb === undefined) return a.localeCompare(b);
        if (ta === undefined) return 1;
        if (tb === undefined) return -1;
        return tb - ta;
      }),
    );
    const order: Array<FileTypeKey | "Other"> = [
      "MGT-7",
      "PAS-3",
      "MGT-14",
      "AOC-4",
      "MOA",
      "SH-7",
      "SH-8",
      "SH-9",
      "SH-11",
      "Other",
    ];
    return order
      .filter((t) => groups.has(t))
      .map((type) => ({ type, paths: groups.get(type) as string[] }));
  }, [selectedFiles, fileDates]);

  // Captable extraction: derive MGT-7 + PAS-3 keys from the current selection
  const selectedMgt7 = useMemo(() => {
    const mgt7Files = Array.from(selectedFiles).filter(
      (f) => classifyFile(f) === "MGT-7",
    );
    return mgt7Files[0] ?? null;
  }, [selectedFiles]);

  const selectedPas3s = useMemo(
    () => Array.from(selectedFiles).filter((f) => classifyFile(f) === "PAS-3"),
    [selectedFiles],
  );

  const captableReady = !!selectedMgt7 && selectedPas3s.length > 0;

  const enqueueRun = useCaptableStore((s) => s.enqueueRun);
  const cancelRun = useCaptableStore((s) => s.cancelRun);
  const captableRunsForCin = useCaptableStore((s) =>
    Object.values(s.runs).filter((r) => r.cin === cin),
  );

  const captableInFlight = useMemo(
    () => captableRunsForCin.filter(isCaptableRunInFlight),
    [captableRunsForCin],
  );

  const captableCompleted = useMemo(
    () =>
      captableRunsForCin
        .filter((r) => r.status === "COMPLETED" && r.resultUrl)
        .sort((a, b) => b.requestedAt - a.requestedAt),
    [captableRunsForCin],
  );

  const captableFailed = useMemo(
    () =>
      captableRunsForCin
        .filter(
          (r) => r.status === "FAILED" && !isCaptableRunCancelled(r.error),
        )
        .sort((a, b) => b.requestedAt - a.requestedAt),
    [captableRunsForCin],
  );

  const captableCancelled = useMemo(
    () =>
      captableRunsForCin
        .filter((r) => r.status === "FAILED" && isCaptableRunCancelled(r.error))
        .sort((a, b) => b.requestedAt - a.requestedAt),
    [captableRunsForCin],
  );

  const [captableError, setCaptableError] = useState<string | null>(null);
  const [captableNotice, setCaptableNotice] = useState<string | null>(null);
  const [captableStatsPopup, setCaptableStatsPopup] =
    useState<CaptableExtractionStats | null>(null);

  const queryClient = useQueryClient();

  // Rerun states keyed by source runId.
  const { mutateAsync: doRerunMgt7 } = useRerunMgt7();
  const { mutateAsync: doRerunPas3 } = useRerunPas3();
  const [rerunStates, setRerunStates] = useState<
    Record<string, "running" | "success" | "error">
  >({});
  const [rerunErrors, setRerunErrors] = useState<Record<string, string>>({});
  const [rerunPas3States, setRerunPas3States] = useState<
    Record<string, "running" | "success" | "error">
  >({});
  const [rerunPas3Errors, setRerunPas3Errors] = useState<
    Record<string, string>
  >({});

  // Cancel-captable-run error keyed by runId (the optimistic "Cancelling…"
  // state itself lives on the store entry as `cancelling`).
  const [cancelErrors, setCancelErrors] = useState<Record<string, string>>({});

  const pas3RunLookup = useMemo(
    () => buildPas3RunLookup(latestCaptableRun, cin),
    [latestCaptableRun, cin],
  );

  const pas3StatusByPath = useMemo(() => {
    const map = new Map<string, Pas3RunFileStatus>();
    files.forEach((path) => {
      if (classifyFile(path) !== "PAS-3") return;
      map.set(path, getPas3StatusForPath(path, pas3RunLookup));
    });
    return map;
  }, [files, pas3RunLookup]);

  const latestPas3IssueCount = useMemo(
    () =>
      Array.from(pas3StatusByPath.values()).filter((s) => s.issues.length > 0)
        .length,
    [pas3StatusByPath],
  );

  const pas3IssuePaths = useMemo(
    () =>
      Array.from(pas3StatusByPath.entries())
        .filter(([, status]) => status.issues.length > 0)
        .map(([path]) => path),
    [pas3StatusByPath],
  );

  const handleCancelCaptableRun = async (runId: string) => {
    setCancelErrors((prev) => {
      const n = { ...prev };
      delete n[runId];
      return n;
    });
    const res = await cancelRun(runId);
    if (res.kind === "error") {
      setCancelErrors((prev) => ({ ...prev, [runId]: res.error }));
    }
    // "cancelled"/"alreadyFinished" need no extra UI work — the store entry's
    // status + `cancelling` flag drive the badge, and polling resolves the rest.
  };

  const anyRerunQueued =
    Object.values(rerunStates).some((s) => s === "success") ||
    Object.values(rerunPas3States).some((s) => s === "success");

  // Once the server reflects a completed/failed run, stop showing "queued" for
  // the source run that triggered it and stop polling.
  useEffect(() => {
    if (
      !anyRerunQueued ||
      !latestCaptableRun ||
      latestCaptableRun.status === "QUEUED" ||
      latestCaptableRun.status === "RUNNING"
    )
      return;
    setRerunStates({});
    setRerunPas3States({});
  }, [latestCaptableRun?.status, anyRerunQueued]);

  // Poll listS3Files every 5 s while a rerun is queued so the parent's
  // latestCaptableRun prop gets updated with the new run's status.
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (anyRerunQueued) {
      pollRef.current = setInterval(() => {
        queryClient.invalidateQueries("listS3Files");
      }, 5000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [anyRerunQueued, queryClient]);

  const handleRerunMgt7 = async (runId: string) => {
    setRerunStates((prev) => ({ ...prev, [runId]: "running" }));
    setRerunErrors((prev) => {
      const n = { ...prev };
      delete n[runId];
      return n;
    });
    try {
      const payload = selectedMgt7
        ? { mgt7: { key: toS3Key(selectedMgt7) } }
        : {};
      await doRerunMgt7({ sourceRunId: runId, payload });
      setRerunStates((prev) => ({ ...prev, [runId]: "success" }));
      queryClient.invalidateQueries("listS3Files");
    } catch (err: any) {
      setRerunStates((prev) => ({ ...prev, [runId]: "error" }));
      setRerunErrors((prev) => ({
        ...prev,
        [runId]:
          err?.response?.data?.message ?? err?.message ?? "Rerun failed.",
      }));
    }
  };

  const handleRerunPas3 = async (runId: string) => {
    setRerunPas3States((prev) => ({ ...prev, [runId]: "running" }));
    setRerunPas3Errors((prev) => {
      const n = { ...prev };
      delete n[runId];
      return n;
    });
    try {
      const payload =
        selectedPas3s.length > 0
          ? {
              pas3: selectedPas3s.map((file) => ({ key: toS3Key(file) })),
            }
          : {};
      await doRerunPas3({ sourceRunId: runId, payload });
      setRerunPas3States((prev) => ({ ...prev, [runId]: "success" }));
      queryClient.invalidateQueries("listS3Files");
    } catch (err: any) {
      setRerunPas3States((prev) => ({ ...prev, [runId]: "error" }));
      setRerunPas3Errors((prev) => ({
        ...prev,
        [runId]:
          err?.response?.data?.message ?? err?.message ?? "PAS-3 rerun failed.",
      }));
    }
  };

  const handleExtractCaptable = async () => {
    if (!selectedMgt7 || selectedPas3s.length === 0) return;
    setCaptableError(null);
    setCaptableNotice(null);
    const result = await enqueueRun({
      cin,
      // Pair each key with the filing date parsed from its filename so the
      // backend can pick the latest PAS-3. date is null when none was detected.
      mgt7: {
        key: toS3Key(selectedMgt7),
        date: toISODate(fileDates.get(selectedMgt7) ?? null),
      },
      pas3: selectedPas3s.map((f) => ({
        key: toS3Key(f),
        date: toISODate(fileDates.get(f) ?? null),
      })),
    });
    if (result.kind === "error") {
      setCaptableError(result.error);
      toast(result.error, { type: "error", autoClose: 4000 });
    } else if (result.kind === "running") {
      setCaptableNotice("Captable extraction already in progress.");
      toast("Captable extraction already in progress.", {
        type: "info",
        autoClose: 3500,
      });
    } else {
      // The in-flight status box renders below the fold, so surface a toast
      // to confirm the run started without the user having to scroll.
      toast("Captable extraction started — see progress below.", {
        type: "success",
        autoClose: 3500,
      });
    }
  };

  useEffect(() => {
    if (captableNotice) {
      const t = setTimeout(() => setCaptableNotice(null), 3500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [captableNotice]);

  // Financial extraction: AOC-4 files only
  const selectedAoc4s = useMemo(
    () => Array.from(selectedFiles).filter((f) => classifyFile(f) === "AOC-4"),
    [selectedFiles],
  );

  const financialReady = selectedAoc4s.length > 0;

  const enqueueFinancialRun = useFinancialStore((s) => s.enqueueRun);
  const financialRunsForCin = useFinancialStore((s) =>
    Object.values(s.runs).filter((r) => r.cin === cin),
  );

  const financialInFlight = useMemo(
    () => financialRunsForCin.filter(isFinancialRunInFlight),
    [financialRunsForCin],
  );

  const financialCompleted = useMemo(
    () =>
      financialRunsForCin
        .filter((r) => r.status === "COMPLETED" && r.resultUrl)
        .sort((a, b) => b.requestedAt - a.requestedAt),
    [financialRunsForCin],
  );

  const financialFailed = useMemo(
    () =>
      financialRunsForCin
        .filter((r) => r.status === "FAILED")
        .sort((a, b) => b.requestedAt - a.requestedAt),
    [financialRunsForCin],
  );

  const [financialError, setFinancialError] = useState<string | null>(null);
  const [financialNotice, setFinancialNotice] = useState<string | null>(null);

  const handleExtractFinancials = async () => {
    if (selectedAoc4s.length === 0) return;
    setFinancialError(null);
    setFinancialNotice(null);
    const result = await enqueueFinancialRun({
      cin,
      aoc4Keys: selectedAoc4s.map(toS3Key),
    });
    if (result.kind === "error") {
      setFinancialError(result.error);
      toast(result.error, { type: "error", autoClose: 4000 });
    } else if (result.kind === "running") {
      setFinancialNotice("Financial extraction already in progress.");
      toast("Financial extraction already in progress.", {
        type: "info",
        autoClose: 3500,
      });
    } else {
      // The in-flight status box renders below the fold, so surface a toast
      // to confirm the run started without the user having to scroll.
      toast("Financial extraction started — see progress below.", {
        type: "success",
        autoClose: 3500,
      });
    }
  };

  // High-quality financial rerun (GPT-5 + OCR). Re-extracts all AOC-4s of the
  // source run; the new run is tracked in the store and polled like any other.
  const rerunFinancialRun = useFinancialStore((s) => s.rerunRun);
  const [financialRerunStates, setFinancialRerunStates] = useState<
    Record<string, "running" | "success" | "error">
  >({});
  const [financialRerunErrors, setFinancialRerunErrors] = useState<
    Record<string, string>
  >({});

  const handleRerunFinancial = async (sourceRunId: string, runCin: string) => {
    setFinancialRerunStates((p) => ({ ...p, [sourceRunId]: "running" }));
    setFinancialRerunErrors((p) => {
      const { [sourceRunId]: _removed, ...rest } = p;
      return rest;
    });
    const result = await rerunFinancialRun(sourceRunId, runCin);
    if (result.kind === "error") {
      setFinancialRerunStates((p) => ({ ...p, [sourceRunId]: "error" }));
      setFinancialRerunErrors((p) => ({ ...p, [sourceRunId]: result.error }));
      toast(result.error, { type: "error", autoClose: 4000 });
      return;
    }
    setFinancialRerunStates((p) => ({ ...p, [sourceRunId]: "success" }));
    toast(
      result.kind === "running"
        ? "Financial rerun already in progress."
        : "Financial rerun started (high quality) — see progress above.",
      { type: result.kind === "running" ? "info" : "success", autoClose: 3500 },
    );
  };

  useEffect(() => {
    if (financialNotice) {
      const t = setTimeout(() => setFinancialNotice(null), 3500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [financialNotice]);

  const downloadFinancial = (url: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadCaptable = (url: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [duplicateNotice, setDuplicateNotice] = useState<string | null>(null);
  const [directDownloads, setDirectDownloads] = useState<Set<string>>(
    () => new Set(),
  );

  const requestDownload = useDownloadsStore((s) => s.requestDownload);
  const markTriggered = useDownloadsStore((s) => s.markTriggered);
  const jobsForCin = useDownloadsStore((s) =>
    Object.values(s.jobs).filter((j) => j.cin === cin),
  );

  const inFlightJobs = useMemo(
    () => jobsForCin.filter(isJobInFlight),
    [jobsForCin],
  );

  const readyJobs = useMemo(
    () =>
      jobsForCin
        .filter((j) => j.state === "completed" && !!j.fileUrl)
        .sort((a, b) => b.requestedAt - a.requestedAt),
    [jobsForCin],
  );

  const inFlightRequestKeys = useMemo(
    () => new Set(inFlightJobs.map((j) => j.requestKey)),
    [inFlightJobs],
  );

  const downloadFromUrl = (url: string, jobId: string) => {
    // User-gesture-driven anchor click — same tab navigation lets the S3
    // signed URL's Content-Disposition trigger a download without popup
    // blockers interfering.
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    markTriggered(jobId);
  };

  useEffect(() => {
    if (duplicateNotice) {
      const t = setTimeout(() => setDuplicateNotice(null), 3500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [duplicateNotice]);

  // Tree paths are relative to the company folder; S3 keys include the CIN
  // prefix (same convention as FileExplorer's signed-URL fetch).
  const toS3Key = (treeKey: string) => `${cin}/${treeKey}`;

  const downloadSingleFile = async (file: string) => {
    setDirectDownloads((prev) => new Set(prev).add(file));
    try {
      const signedUrl = await getMcaFileSignedUrl(toS3Key(file));
      const response = await fetch(signedUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file (HTTP ${response.status}).`);
      }

      const blobUrl = URL.createObjectURL(await response.blob());
      const fileName = file.split("/").filter(Boolean).pop() ?? file;
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
    } catch (err: any) {
      setDownloadError(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to download file.",
      );
    } finally {
      setDirectDownloads((prev) => {
        const next = new Set(prev);
        next.delete(file);
        return next;
      });
    }
  };

  const startDownload = async (keys: string[]) => {
    if (keys.length === 0) return;
    setDownloadError(null);
    setDuplicateNotice(null);
    if (keys.length === 1) {
      await downloadSingleFile(keys[0]);
      return;
    }
    const s3Keys = keys.map(toS3Key);
    const result = await requestDownload(cin, s3Keys);
    if (result.kind === "error") {
      setDownloadError(result.error);
      return;
    }
    if (result.kind === "duplicate") {
      setDuplicateNotice(
        `This set is already preparing (job ${result.entry.jobId}).`,
      );
      return;
    }
    if (result.kind === "replay" && result.entry.fileUrl) {
      // Click came from a real user gesture, so it's safe to download now.
      downloadFromUrl(result.entry.fileUrl, result.entry.jobId);
      setDuplicateNotice("Re-using the previously prepared zip.");
    }
  };

  const handleDownloadSelected = () => {
    startDownload(Array.from(selectedFiles));
  };

  const handleDownloadOne = (file: string) => {
    startDownload([file]);
  };

  const handleViewFile = async (path: string) => {
    setViewingFiles((prev) => new Set(Array.from(prev).concat(path)));
    try {
      const url = await getMcaFileSignedUrl(toS3Key(path));
      const fileName = path.split("/").filter(Boolean).pop() ?? path;
      setPdfViewerHistory((prev) => (pdfViewer ? [...prev, pdfViewer] : []));
      setPdfViewer({ url, fileName });
    } catch {
      // intentionally silent — the download button is the fallback
    } finally {
      setViewingFiles((prev) => {
        const next = new Set(prev);
        next.delete(path);
        return next;
      });
    }
  };

  const handleShowAttachments = async (path: string) => {
    setAttachmentsState({
      file: path,
      loading: true,
      attachments: [],
      error: null,
    });
    try {
      const result = await extractPdfAttachments(toS3Key(path));
      setAttachmentsState((prev) =>
        prev
          ? { ...prev, loading: false, attachments: result.attachments }
          : null,
      );
    } catch (e: unknown) {
      const msg = (e as Error)?.message ?? "Failed to extract attachments";
      setAttachmentsState((prev) =>
        prev ? { ...prev, loading: false, error: msg } : null,
      );
    }
  };

  const downloadAttachment = (attachment: PdfAttachment) => {
    const byteStr = atob(attachment.data);
    const bytes = new Uint8Array(byteStr.length);
    for (let i = 0; i < byteStr.length; i++) {
      bytes[i] = byteStr.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: attachment.mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = attachment.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleViewAttachment = (attachment: PdfAttachment) => {
    // Push the parent file as the previous entry so "Previous" navigates back to it.
    const parentEntries: PdfEntry[] = [];
    if (pdfViewer) parentEntries.push(...pdfViewerHistory, pdfViewer);
    else if (attachmentsState) {
      parentEntries.push(...pdfViewerHistory, {
        s3Key: toS3Key(attachmentsState.file),
        fileName:
          attachmentsState.file.split("/").filter(Boolean).pop() ??
          attachmentsState.file,
      });
    }
    setPdfViewerHistory(parentEntries);
    setPdfViewer({
      fileName: attachment.name,
      base64Data: { data: attachment.data, mimeType: attachment.mimeType },
    });
  };

  const handleGoBackPdf = async () => {
    const prev = pdfViewerHistory[pdfViewerHistory.length - 1];
    if (!prev) return;
    setPdfViewerHistory((h) => h.slice(0, -1));
    if (prev.s3Key && !prev.url) {
      setNavigatingBack(true);
      try {
        const url = await getMcaFileSignedUrl(prev.s3Key);
        setPdfViewer({ ...prev, url });
      } catch {
        setPdfViewer(null);
      } finally {
        setNavigatingBack(false);
      }
    } else {
      setPdfViewer(prev);
    }
  };

  const selectedRequestKey = useMemo(
    () =>
      selectedFiles.size > 1
        ? makeRequestKey(cin, Array.from(selectedFiles).map(toS3Key))
        : null,
    [cin, selectedFiles],
  );

  const selectedIsInFlight =
    selectedFiles.size === 1
      ? directDownloads.has(Array.from(selectedFiles)[0])
      : !!selectedRequestKey && inFlightRequestKeys.has(selectedRequestKey);

  const isFileInFlight = (file: string) =>
    directDownloads.has(file) ||
    inFlightRequestKeys.has(makeRequestKey(cin, [toS3Key(file)]));

  const recentFailure: DownloadEntry | undefined = useMemo(
    () =>
      jobsForCin
        .filter((j) => j.state === "failed" && j.error)
        .sort((a, b) => b.requestedAt - a.requestedAt)[0],
    [jobsForCin],
  );

  const failureMessage = downloadError ?? recentFailure?.error ?? null;

  return (
    <>
      <Transition.Root show={showPopup} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={pdfViewer ? () => {} : onClose}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-10/12 h-[90vh] max-h-[90vh] transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all flex flex-col">
                  <Dialog.Title
                    as="h3"
                    className="text-base font-semibold leading-6 text-gray-900 shrink-0"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col text-left">
                        <h2>Company Files</h2>
                        <p className="text-xs text-gray-500 font-normal">
                          {companyName ?? "Unknown"}{" "}
                          <span className="text-gray-400">· {cin}</span>
                        </p>
                      </div>
                      <X
                        className="text-gray-500 hover:text-primary cursor-pointer"
                        onClick={onClose}
                      />
                    </div>
                  </Dialog.Title>

                  <div className="mt-4 text-left flex-1 min-h-0 flex flex-col">
                    <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                      <div className="flex gap-2 flex-wrap border-b border-gray-200 pb-2">
                        {FILE_TYPE_TABS.map((tab) => {
                          const count =
                            tab === "ALL"
                              ? files.length
                              : files.filter((f) => classifyFile(f) === tab)
                                  .length;
                          const isActive = activeTab === tab;
                          return (
                            <button
                              key={tab}
                              onClick={() => handleTabChange(tab)}
                              title={FORM_TYPE_LABELS[tab]}
                              className={`px-3 py-1 text-xs rounded-full border ${
                                isActive
                                  ? "bg-blue-600 text-white border-blue-600"
                                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                              }`}
                            >
                              {tab}{" "}
                              <span
                                className={`ml-1 ${
                                  isActive ? "text-blue-100" : "text-gray-400"
                                }`}
                              >
                                ({count})
                              </span>
                            </button>
                          );
                        })}
                        <button
                          onClick={() => handleTabChange("SELECTED")}
                          className={`px-3 py-1 text-xs rounded-full border ${
                            activeTab === "SELECTED"
                              ? "bg-blue-600 text-white border-blue-600"
                              : selectedFiles.size > 0
                                ? "bg-white text-blue-700 border-blue-400 hover:bg-blue-50"
                                : "bg-white text-gray-400 border-gray-200"
                          }`}
                        >
                          Selected{" "}
                          <span
                            className={`ml-1 ${
                              activeTab === "SELECTED"
                                ? "text-blue-100"
                                : "text-gray-400"
                            }`}
                          >
                            ({selectedFiles.size})
                          </span>
                        </button>
                      </div>

                      {activeTab !== "SELECTED" && (
                        <>
                          <div className="flex items-center gap-2 mt-4">
                            <div className="relative flex-1">
                              <input
                                type="text"
                                placeholder="Search files..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full border border-gray-300 rounded px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                              />
                              {searchTerm && (
                                <button
                                  type="button"
                                  onClick={() => setSearchTerm("")}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                >
                                  <CircleX className="w-4 h-4 hover:text-blue-700" />
                                </button>
                              )}
                            </div>
                            <button
                              onClick={pickMgt7AndPas3}
                              disabled={!pairAvailable}
                              title="Select the latest MGT-7 and every PAS-3 (one per filing date — highest-numbered copy wins on duplicates)"
                              className={`px-3 py-2 text-xs rounded border whitespace-nowrap font-medium ${
                                !pairAvailable
                                  ? "border-gray-200 text-gray-300 cursor-not-allowed"
                                  : "border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
                              }`}
                            >
                              Pick MGT-7 + all PAS-3
                            </button>
                            {activeTab === "AOC-4" && (
                              <button
                                onClick={pickAllAoc4}
                                disabled={!aoc4Available}
                                title="Select every AOC-4 (one per filing date — highest-numbered copy wins on duplicates)"
                                className={`px-3 py-2 text-xs rounded border whitespace-nowrap font-medium ${
                                  !aoc4Available
                                    ? "border-gray-200 text-gray-300 cursor-not-allowed"
                                    : "border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700"
                                }`}
                              >
                                Pick all AOC-4 ({aoc4Selection.keptPaths.length}
                                )
                              </button>
                            )}
                            <button
                              onClick={
                                activeTab === "PAS-3"
                                  ? pickPas3WithIssues
                                  : pickLatest
                              }
                              disabled={
                                activeTab === "PAS-3"
                                  ? pas3IssuePaths.length === 0
                                  : latestByType.size === 0
                              }
                              title={
                                activeTab === "PAS-3"
                                  ? pas3IssuePaths.length > 0
                                    ? `Select ${pas3IssuePaths.length} PAS-3 file${pas3IssuePaths.length === 1 ? "" : "s"} with issues from the last run`
                                    : "No PAS-3 issues found in the last run"
                                  : activeTab === "ALL"
                                    ? "Select the most recent file for each form type"
                                    : `Select the most recent ${activeTab}`
                              }
                              className={`px-3 py-2 text-xs rounded border whitespace-nowrap ${
                                activeTab === "PAS-3"
                                  ? pas3IssuePaths.length === 0
                                    ? "border-gray-200 text-gray-300 cursor-not-allowed"
                                    : "border-blue-300 text-blue-700 hover:bg-blue-50"
                                  : latestByType.size === 0
                                    ? "border-gray-200 text-gray-300 cursor-not-allowed"
                                    : "border-blue-300 text-blue-700 hover:bg-blue-50"
                              }`}
                            >
                              {activeTab === "PAS-3"
                                ? `Pick PAS-3 with issues (${pas3IssuePaths.length})`
                                : activeTab === "ALL"
                                  ? "Pick latest of each"
                                  : `Pick latest ${activeTab}`}
                            </button>
                            <button
                              onClick={toggleAllVisible}
                              disabled={visibleFiles.length === 0}
                              className={`px-3 py-2 text-xs rounded border whitespace-nowrap ${
                                visibleFiles.length === 0
                                  ? "border-gray-200 text-gray-300 cursor-not-allowed"
                                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {allVisibleSelected ? "Clear all" : "Select all"}
                            </button>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-[11px] text-gray-500">
                              Date range
                            </span>
                            <input
                              type="date"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                              max={endDate || undefined}
                              className="border border-gray-300 rounded px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              title="From date"
                            />
                            <span className="text-[11px] text-gray-400">
                              to
                            </span>
                            <input
                              type="date"
                              value={endDate}
                              onChange={(e) => setEndDate(e.target.value)}
                              min={startDate || undefined}
                              className="border border-gray-300 rounded px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              title="To date"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setStartDate("");
                                setEndDate("");
                              }}
                              disabled={!hasDateRange}
                              className={`px-2 py-1 text-[11px] rounded border ${
                                hasDateRange
                                  ? "border-gray-300 text-gray-700 hover:bg-gray-50"
                                  : "border-gray-200 text-gray-300 cursor-not-allowed"
                              }`}
                            >
                              Clear dates
                            </button>
                          </div>
                          {hasDateRange && (
                            <div className="mt-2 px-3 py-1.5 rounded text-[11px] bg-blue-50 text-blue-700 border border-blue-200">
                              Date filter is active. Files without a detected
                              date are filtered out
                              {undatedExcludedCount > 0
                                ? ` (${undatedExcludedCount} hidden).`
                                : "."}
                            </div>
                          )}
                        </>
                      )}

                      {mgt7Pas3Selection.superseded.size > 0 &&
                        activeTab === "PAS-3" && (
                          <div className="mt-2 px-3 py-1.5 rounded text-[11px] bg-amber-50 text-amber-700 border border-amber-200">
                            {mgt7Pas3Selection.superseded.size} duplicate PAS-3
                            (same filing date) skipped — keeping the
                            highest-numbered copy per date.
                          </div>
                        )}

                      {aoc4Selection.superseded.size > 0 &&
                        activeTab === "AOC-4" && (
                          <div className="mt-2 px-3 py-1.5 rounded text-[11px] bg-amber-50 text-amber-700 border border-amber-200">
                            {aoc4Selection.superseded.size} duplicate AOC-4
                            (same filing date) flagged — keeping the
                            highest-numbered copy per date.
                          </div>
                        )}

                      {activeTab === "SELECTED" ? (
                        <div className="mt-3 h-[55vh] overflow-y-auto border border-gray-200 rounded">
                          {selectedSummary.length === 0 ? (
                            <div className="text-sm text-gray-400 italic py-8 text-center">
                              No files selected yet.
                            </div>
                          ) : (
                            <table className="w-full text-xs">
                              <tbody>
                                {selectedSummary.map(({ type, paths }) => (
                                  <>
                                    <tr
                                      key={`hdr-${type}`}
                                      className="bg-gray-50 border-b border-gray-200"
                                    >
                                      <td
                                        colSpan={3}
                                        className="px-3 py-1.5 font-semibold text-gray-600"
                                      >
                                        {type}{" "}
                                        <span className="font-normal text-gray-400">
                                          ({paths.length})
                                        </span>
                                      </td>
                                    </tr>
                                    {paths.map((path) => {
                                      const name =
                                        path.split("/").filter(Boolean).pop() ??
                                        path;
                                      const date = formatExtractedDate(
                                        fileDates.get(path) ?? null,
                                      );
                                      return (
                                        <tr
                                          key={path}
                                          className="border-b border-gray-100 hover:bg-gray-50"
                                        >
                                          <td
                                            className="pl-4 pr-2 py-1.5 text-gray-700 max-w-0 w-full"
                                            title={path}
                                          >
                                            <span className="flex items-center gap-1.5">
                                              <FileText className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                                              <span className="truncate">
                                                {name}
                                              </span>
                                            </span>
                                          </td>
                                          <td className="px-2 py-1.5 text-gray-400 whitespace-nowrap tabular-nums text-right">
                                            {date}
                                          </td>
                                          <td className="pl-2 pr-3 py-1.5">
                                            <button
                                              type="button"
                                              onClick={() => toggleFile(path)}
                                              title="Remove"
                                              className="text-gray-300 hover:text-red-500"
                                            >
                                              <X className="w-3.5 h-3.5" />
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      ) : attachmentsState ? (
                        <div className="mt-3 h-[55vh] border border-gray-200 rounded flex flex-col overflow-hidden">
                          <div className="flex justify-between items-center px-3 py-2 border-b border-gray-200 bg-gray-50 shrink-0">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Paperclip className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                              <span className="text-xs font-semibold text-gray-700 truncate">
                                Attachments:{" "}
                                {attachmentsState.file
                                  .split("/")
                                  .filter(Boolean)
                                  .pop()}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setAttachmentsState(null)}
                              className="text-gray-400 hover:text-gray-700 shrink-0 ml-2"
                              title="Back to file list"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex-1 overflow-y-auto">
                            {attachmentsState.loading ? (
                              <div className="py-8 flex justify-center">
                                <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
                              </div>
                            ) : attachmentsState.error ? (
                              <div className="px-4 py-4 text-xs text-red-600">
                                {attachmentsState.error}
                              </div>
                            ) : attachmentsState.attachments.length === 0 ? (
                              <div className="px-4 py-8 text-xs text-gray-400 italic text-center">
                                No embedded attachments found in this file.
                              </div>
                            ) : (
                              <div className="divide-y divide-gray-100">
                                {attachmentsState.attachments.map((att, i) => {
                                  const isPdf =
                                    att.mimeType === "application/pdf" ||
                                    att.name.toLowerCase().endsWith(".pdf");
                                  return (
                                    <div
                                      key={i}
                                      className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50"
                                    >
                                      <FileText className="w-4 h-4 text-blue-400 shrink-0" />
                                      <span
                                        className="flex-1 text-xs text-gray-700 truncate"
                                        title={att.name}
                                      >
                                        {att.name}
                                      </span>
                                      <span className="text-[10px] text-gray-400 shrink-0">
                                        {att.mimeType.split("/").pop()}
                                      </span>
                                      {isPdf && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleViewAttachment(att)
                                          }
                                          className="p-1 rounded hover:bg-blue-100"
                                          title="View"
                                        >
                                          <Eye className="w-3.5 h-3.5 text-blue-700" />
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => downloadAttachment(att)}
                                        className="p-1 rounded hover:bg-blue-100"
                                        title="Download attachment"
                                      >
                                        <Download className="w-3.5 h-3.5 text-blue-700" />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div
                          key={visibleListKey}
                          className="mt-3 h-[55vh] overflow-y-auto border border-gray-200 rounded"
                        >
                          {!isFetched ? (
                            <div className="py-8">
                              <Loader />
                            </div>
                          ) : visibleFiles.length === 0 ? (
                            <div className="text-sm text-gray-400 italic py-8 text-center">
                              No files match.
                            </div>
                          ) : activeTab !== "ALL" ? (
                            <div className="py-1 text-sm">
                              {renderFlatList(
                                sortedVisibleFiles,
                                selectedFiles,
                                toggleFile,
                                handleDownloadOne,
                                isFileInFlight,
                                fileDates,
                                latestByType,
                                dupMarks,
                                pas3StatusByPath,
                                handleViewFile,
                                handleShowAttachments,
                                viewingFiles,
                              )}
                            </div>
                          ) : (
                            <div className="py-1 text-sm">
                              {renderTreeNodes(
                                visibleTree,
                                0,
                                expanded,
                                toggleFolder,
                                selectedFiles,
                                toggleFile,
                                toggleFolderSelection,
                                handleDownloadOne,
                                isFileInFlight,
                                fileDates,
                                latestByType,
                                dupMarks,
                                pas3StatusByPath,
                                handleViewFile,
                                handleShowAttachments,
                                viewingFiles,
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {readyJobs.length > 0 && (
                        <div className="mt-3 px-3 py-2 rounded text-xs bg-green-50 text-green-800 border border-green-200">
                          <div className="font-medium mb-1">
                            {readyJobs.length} download
                            {readyJobs.length === 1 ? "" : "s"} ready
                          </div>
                          <ul className="space-y-1">
                            {readyJobs.map((j) => (
                              <li
                                key={j.jobId}
                                className="flex items-center justify-between gap-2"
                              >
                                <span className="truncate">
                                  Job {j.jobId} · {j.keys.length} file
                                  {j.keys.length === 1 ? "" : "s"}
                                  {j.triggered ? " · downloaded" : ""}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    j.fileUrl &&
                                    downloadFromUrl(j.fileUrl, j.jobId)
                                  }
                                  className="px-2 py-0.5 rounded bg-green-600 text-white hover:bg-green-700 flex items-center gap-1"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                  {j.triggered
                                    ? "Download again"
                                    : "Download zip"}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {inFlightJobs.length > 0 && (
                        <div className="mt-3 px-3 py-2 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">
                          <div className="font-medium">
                            Preparing {inFlightJobs.length} download
                            {inFlightJobs.length === 1 ? "" : "s"}…
                          </div>
                          <ul className="mt-1 space-y-0.5">
                            {inFlightJobs.map((j) => (
                              <li key={j.jobId} className="truncate">
                                Job {j.jobId} · {j.keys.length} file
                                {j.keys.length === 1 ? "" : "s"} · {j.state}
                                {j.progress ? ` (${j.progress}%)` : ""}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {duplicateNotice && (
                        <div className="mt-3 px-3 py-2 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">
                          {duplicateNotice}
                        </div>
                      )}
                      {failureMessage && (
                        <div className="mt-3 px-3 py-2 rounded text-xs bg-red-50 text-red-700 border border-red-200">
                          Download failed: {failureMessage}
                        </div>
                      )}

                      {latestCaptableRun?.status === "COMPLETED" &&
                        latestCaptableRun.resultUrl && (
                          <div className="mt-3 px-3 py-2 rounded text-xs bg-purple-50 text-purple-800 border border-purple-200">
                            <div className="font-medium mb-1">
                              Latest captable
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center justify-between gap-2">
                                <span className="truncate">
                                  run {latestCaptableRun.id.slice(0, 8)}
                                </span>
                                <div className="flex items-center gap-1 shrink-0">
                                  {latestCaptableRun.extractionStats && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setCaptableStatsPopup(
                                          latestCaptableRun.extractionStats,
                                        )
                                      }
                                      className="p-0.5 rounded text-purple-600 hover:text-purple-800 hover:bg-purple-100"
                                      title="View extraction stats"
                                    >
                                      <Info className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    disabled={
                                      rerunStates[latestCaptableRun.id] ===
                                      "running"
                                    }
                                    onClick={() =>
                                      handleRerunMgt7(latestCaptableRun.id)
                                    }
                                    title={
                                      selectedMgt7
                                        ? `Rerun using selected MGT-7: ${selectedMgt7.split("/").pop()}`
                                        : "Rerun using original MGT-7"
                                    }
                                    className="px-2 py-0.5 rounded border border-teal-500 text-teal-700 hover:bg-teal-50 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {rerunStates[latestCaptableRun.id] ===
                                    "running" ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                      <RefreshCw className="w-3.5 h-3.5" />
                                    )}
                                    Rerun MGT-7
                                  </button>
                                  <button
                                    type="button"
                                    disabled={
                                      rerunPas3States[latestCaptableRun.id] ===
                                      "running"
                                    }
                                    onClick={() =>
                                      handleRerunPas3(latestCaptableRun.id)
                                    }
                                    title={
                                      selectedPas3s.length > 0
                                        ? `Rerun using ${selectedPas3s.length} selected PAS-3 file${selectedPas3s.length === 1 ? "" : "s"}`
                                        : "Rerun all PAS-3 from this run"
                                    }
                                    className="px-2 py-0.5 rounded border border-indigo-500 text-indigo-700 hover:bg-indigo-50 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {rerunPas3States[latestCaptableRun.id] ===
                                    "running" ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                      <RefreshCw className="w-3.5 h-3.5" />
                                    )}
                                    Rerun PAS-3
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      downloadCaptable(
                                        latestCaptableRun.resultUrl!,
                                      )
                                    }
                                    className="px-2 py-0.5 rounded bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-1"
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                    Download xlsx
                                  </button>
                                  {latestCaptableRun.stage4Url && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        downloadCaptable(
                                          latestCaptableRun.stage4Url!,
                                        )
                                      }
                                      title="Download the generated Stage-4 onboarding workbook (draft)"
                                      className="px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1"
                                    >
                                      <Download className="w-3.5 h-3.5" />
                                      Stage 4 xlsx
                                    </button>
                                  )}
                                </div>
                              </div>
                              {rerunStates[latestCaptableRun.id] ===
                                "success" && (
                                <p className="text-[11px] text-teal-700">
                                  MGT-7 rerun queued - new run will appear
                                  shortly.
                                </p>
                              )}
                              {rerunStates[latestCaptableRun.id] ===
                                "error" && (
                                <p className="text-[11px] text-red-600">
                                  {rerunErrors[latestCaptableRun.id]}
                                </p>
                              )}
                              {rerunPas3States[latestCaptableRun.id] ===
                                "success" && (
                                <p className="text-[11px] text-indigo-700">
                                  PAS-3 rerun queued - new run will appear
                                  shortly.
                                </p>
                              )}
                              {rerunPas3States[latestCaptableRun.id] ===
                                "error" && (
                                <p className="text-[11px] text-red-600">
                                  {rerunPas3Errors[latestCaptableRun.id]}
                                </p>
                              )}
                              {selectedMgt7 &&
                                rerunStates[latestCaptableRun.id] !==
                                  "success" && (
                                  <p className="text-[11px] text-gray-400">
                                    MGT-7 rerun will use:{" "}
                                    {selectedMgt7.split("/").pop()}
                                  </p>
                                )}
                              {selectedPas3s.length > 0 &&
                                rerunPas3States[latestCaptableRun.id] !==
                                  "success" && (
                                  <p className="text-[11px] text-gray-400">
                                    PAS-3 rerun will use {selectedPas3s.length}{" "}
                                    selected file
                                    {selectedPas3s.length === 1 ? "" : "s"}.
                                  </p>
                                )}
                              {latestPas3IssueCount > 0 && (
                                <p className="text-[11px] text-amber-700">
                                  {latestPas3IssueCount} PAS-3 file
                                  {latestPas3IssueCount === 1 ? "" : "s"} have
                                  issues in the last run. Check the PAS-3 tab
                                  for per-file markers.
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                      {captableCompleted.length > 0 && (
                        <div className="mt-3 px-3 py-2 rounded text-xs bg-purple-50 text-purple-800 border border-purple-200">
                          <div className="font-medium mb-1">
                            {captableCompleted.length} captable
                            {captableCompleted.length === 1 ? "" : "s"} ready
                          </div>
                          <ul className="space-y-2">
                            {captableCompleted.map((r) => {
                              const rerunState = rerunStates[r.runId];
                              const rerunPas3State = rerunPas3States[r.runId];
                              return (
                                <li key={r.runId} className="space-y-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="truncate">
                                      {r.cin} · run {r.runId.slice(0, 8)}
                                    </span>
                                    <div className="flex items-center gap-1 shrink-0">
                                      {r.extractionStats && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setCaptableStatsPopup(
                                              r.extractionStats,
                                            )
                                          }
                                          className="p-0.5 rounded text-purple-600 hover:text-purple-800 hover:bg-purple-100"
                                          title="View extraction stats"
                                        >
                                          <Info className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        disabled={rerunState === "running"}
                                        onClick={() => handleRerunMgt7(r.runId)}
                                        title={
                                          selectedMgt7
                                            ? `Rerun using selected MGT-7: ${selectedMgt7.split("/").pop()}`
                                            : "Rerun using original MGT-7"
                                        }
                                        className="px-2 py-0.5 rounded border border-teal-500 text-teal-700 hover:bg-teal-50 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        {rerunState === "running" ? (
                                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                          <RefreshCw className="w-3.5 h-3.5" />
                                        )}
                                        Rerun MGT-7
                                      </button>
                                      <button
                                        type="button"
                                        disabled={rerunPas3State === "running"}
                                        onClick={() => handleRerunPas3(r.runId)}
                                        title={
                                          selectedPas3s.length > 0
                                            ? `Rerun using ${selectedPas3s.length} selected PAS-3 file${selectedPas3s.length === 1 ? "" : "s"}`
                                            : "Rerun all PAS-3 from this run"
                                        }
                                        className="px-2 py-0.5 rounded border border-indigo-500 text-indigo-700 hover:bg-indigo-50 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        {rerunPas3State === "running" ? (
                                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                          <RefreshCw className="w-3.5 h-3.5" />
                                        )}
                                        Rerun PAS-3
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          r.resultUrl &&
                                          downloadCaptable(r.resultUrl)
                                        }
                                        className="px-2 py-0.5 rounded bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-1"
                                      >
                                        <Download className="w-3.5 h-3.5" />
                                        Download xlsx
                                      </button>
                                      {r.stage4Url && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            downloadCaptable(r.stage4Url!)
                                          }
                                          title="Download the generated Stage-4 onboarding workbook (draft)"
                                          className="px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1"
                                        >
                                          <Download className="w-3.5 h-3.5" />
                                          Stage 4 xlsx
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  {rerunState === "success" && (
                                    <p className="text-[11px] text-teal-700 pl-1">
                                      MGT-7 rerun queued - new run will appear
                                      shortly.
                                    </p>
                                  )}
                                  {rerunState === "error" && (
                                    <p className="text-[11px] text-red-600 pl-1">
                                      {rerunErrors[r.runId]}
                                    </p>
                                  )}
                                  {rerunPas3State === "success" && (
                                    <p className="text-[11px] text-indigo-700 pl-1">
                                      PAS-3 rerun queued - new run will appear
                                      shortly.
                                    </p>
                                  )}
                                  {rerunPas3State === "error" && (
                                    <p className="text-[11px] text-red-600 pl-1">
                                      {rerunPas3Errors[r.runId]}
                                    </p>
                                  )}
                                  {selectedMgt7 && rerunState !== "success" && (
                                    <p className="text-[11px] text-gray-400 pl-1">
                                      MGT-7 rerun will use:{" "}
                                      {selectedMgt7.split("/").pop()}
                                    </p>
                                  )}
                                  {selectedPas3s.length > 0 &&
                                    rerunPas3State !== "success" && (
                                      <p className="text-[11px] text-gray-400 pl-1">
                                        PAS-3 rerun will use{" "}
                                        {selectedPas3s.length} selected file
                                        {selectedPas3s.length === 1 ? "" : "s"}.
                                      </p>
                                    )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                      {captableInFlight.length > 0 && (
                        <div className="mt-3 px-3 py-2 rounded text-xs bg-indigo-50 text-indigo-700 border border-indigo-200">
                          <div className="font-medium flex items-center gap-1.5">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Extracting captable
                            {captableInFlight.length > 1
                              ? ` (${captableInFlight.length})`
                              : ""}
                            …
                          </div>
                          <ul className="mt-1 space-y-1">
                            {captableInFlight.map((r) => {
                              const cancelStatuses = ["QUEUED", "RUNNING"];
                              const canCancel = cancelStatuses.includes(
                                r.status,
                              );
                              return (
                                <li
                                  key={r.runId}
                                  className="flex flex-col gap-0.5"
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="truncate">
                                      run {r.runId.slice(0, 8)} ·{" "}
                                      {r.cancelling ? "Cancelling…" : r.status}
                                      {r.phase ? ` · ${r.phase}` : ""}
                                    </span>
                                    {canCancel && (
                                      <button
                                        type="button"
                                        disabled={r.cancelling}
                                        onClick={() =>
                                          handleCancelCaptableRun(r.runId)
                                        }
                                        className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium text-red-600 border border-red-200 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        <X className="w-3 h-3" />
                                        {r.cancelling
                                          ? "Cancelling…"
                                          : "Cancel"}
                                      </button>
                                    )}
                                  </div>
                                  {cancelErrors[r.runId] && (
                                    <span className="text-[11px] text-red-600">
                                      {cancelErrors[r.runId]}
                                    </span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                      {captableFailed.length > 0 && (
                        <div className="mt-3 px-3 py-2 rounded text-xs bg-red-50 text-red-700 border border-red-200">
                          <div className="font-medium mb-1">
                            Captable extraction failed
                          </div>
                          {captableFailed.map((r) => (
                            <div key={r.runId} className="truncate">
                              {r.error ?? "Unknown error"}
                            </div>
                          ))}
                        </div>
                      )}
                      {captableCancelled.length > 0 && (
                        <div className="mt-3 px-3 py-2 rounded text-xs bg-gray-100 text-gray-600 border border-gray-200">
                          <div className="font-medium mb-1">
                            Captable extraction cancelled
                          </div>
                          {captableCancelled.map((r) => (
                            <div key={r.runId} className="truncate">
                              run {r.runId.slice(0, 8)} · Cancelled
                            </div>
                          ))}
                        </div>
                      )}
                      {captableError && (
                        <div className="mt-3 px-3 py-2 rounded text-xs bg-red-50 text-red-700 border border-red-200">
                          {captableError}
                        </div>
                      )}
                      {captableNotice && (
                        <div className="mt-3 px-3 py-2 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">
                          {captableNotice}
                        </div>
                      )}

                      {financialCompleted.length > 0 && (
                        <div className="mt-3 px-3 py-2 rounded text-xs bg-emerald-50 text-emerald-800 border border-emerald-200">
                          <div className="font-medium mb-1">
                            {financialCompleted.length} financial template
                            {financialCompleted.length === 1 ? "" : "s"} ready
                          </div>
                          <ul className="space-y-1">
                            {financialCompleted.map((r: FinancialEntry) => (
                              <li
                                key={r.runId}
                                className="flex items-center justify-between gap-2"
                              >
                                <span className="truncate">
                                  {r.cin} · run {r.runId.slice(0, 8)}
                                </span>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <button
                                    type="button"
                                    disabled={
                                      financialRerunStates[r.runId] ===
                                      "running"
                                    }
                                    onClick={() =>
                                      handleRerunFinancial(r.runId, r.cin)
                                    }
                                    title="Re-extract all AOC-4s with the high-quality method (GPT-5 + OCR)"
                                    className="px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60 flex items-center gap-1"
                                  >
                                    {financialRerunStates[r.runId] ===
                                    "running" ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                      <RefreshCw className="w-3.5 h-3.5" />
                                    )}
                                    Rerun (HQ)
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      r.resultUrl &&
                                      downloadFinancial(r.resultUrl)
                                    }
                                    className="px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1"
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                    Download zip
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {financialInFlight.length > 0 && (
                        <div className="mt-3 px-3 py-2 rounded text-xs bg-teal-50 text-teal-700 border border-teal-200">
                          <div className="font-medium flex items-center gap-1.5">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Extracting financials
                            {financialInFlight.length > 1
                              ? ` (${financialInFlight.length})`
                              : ""}
                            …
                          </div>
                          <ul className="mt-1 space-y-0.5">
                            {financialInFlight.map((r: FinancialEntry) => (
                              <li key={r.runId} className="truncate">
                                run {r.runId.slice(0, 8)} · {r.status}
                                {r.phase ? ` · ${r.phase}` : ""}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {financialFailed.length > 0 && (
                        <div className="mt-3 px-3 py-2 rounded text-xs bg-red-50 text-red-700 border border-red-200">
                          <div className="font-medium mb-1">
                            Financial extraction failed
                          </div>
                          {financialFailed.map((r: FinancialEntry) => (
                            <div
                              key={r.runId}
                              className="flex items-center justify-between gap-2 py-0.5"
                            >
                              <span className="truncate">
                                {r.error ?? "Unknown error"}
                              </span>
                              <button
                                type="button"
                                disabled={
                                  financialRerunStates[r.runId] === "running"
                                }
                                onClick={() =>
                                  handleRerunFinancial(r.runId, r.cin)
                                }
                                title="Re-extract all AOC-4s with the high-quality method (GPT-5 + OCR)"
                                className="px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60 flex items-center gap-1 shrink-0"
                              >
                                {financialRerunStates[r.runId] === "running" ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-3.5 h-3.5" />
                                )}
                                Rerun (HQ)
                              </button>
                            </div>
                          ))}
                          {Object.entries(financialRerunErrors).map(
                            ([runId, msg]) => (
                              <div
                                key={runId}
                                className="truncate text-red-600"
                              >
                                {msg}
                              </div>
                            ),
                          )}
                        </div>
                      )}
                      {financialError && (
                        <div className="mt-3 px-3 py-2 rounded text-xs bg-red-50 text-red-700 border border-red-200">
                          {financialError}
                        </div>
                      )}
                      {financialNotice && (
                        <div className="mt-3 px-3 py-2 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">
                          {financialNotice}
                        </div>
                      )}
                    </div>

                    <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200 shrink-0">
                      <p className="text-xs text-gray-500">
                        {selectedFiles.size} selected
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={onClose}
                          className="px-4 py-1 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                        >
                          Close
                        </button>
                        <button
                          disabled={!financialReady}
                          onClick={handleExtractFinancials}
                          title={
                            financialReady
                              ? `Extract financials from ${selectedAoc4s.length} AOC-4 file${selectedAoc4s.length === 1 ? "" : "s"}`
                              : "Select at least one AOC-4 file to extract financials"
                          }
                          className={`px-4 py-1 text-sm rounded flex items-center gap-1 ${
                            financialReady
                              ? "cursor-pointer bg-emerald-600 text-white hover:bg-emerald-700"
                              : "cursor-not-allowed bg-emerald-200 text-white"
                          }`}
                        >
                          Extract Financials
                          {financialReady && (
                            <span className="text-emerald-200 text-xs">
                              ({selectedAoc4s.length} AOC-4)
                            </span>
                          )}
                        </button>
                        <button
                          disabled={!captableReady}
                          onClick={handleExtractCaptable}
                          title={
                            captableReady
                              ? `Extract captable using ${selectedMgt7?.split("/").pop()} + ${selectedPas3s.length} PAS-3`
                              : "Select an MGT-7 and at least one PAS-3 to extract captable"
                          }
                          className={`px-4 py-1 text-sm rounded flex items-center gap-1 ${
                            captableReady
                              ? "cursor-pointer bg-purple-600 text-white hover:bg-purple-700"
                              : "cursor-not-allowed bg-purple-200 text-white"
                          }`}
                        >
                          Extract Captable
                          {captableReady && (
                            <span className="text-purple-200 text-xs">
                              (1 MGT-7 + {selectedPas3s.length} PAS-3)
                            </span>
                          )}
                        </button>
                        <button
                          disabled={
                            selectedFiles.size === 0 || selectedIsInFlight
                          }
                          onClick={handleDownloadSelected}
                          className={`px-4 py-1 text-sm text-white rounded flex items-center gap-1 ${
                            selectedFiles.size === 0 || selectedIsInFlight
                              ? "cursor-not-allowed bg-blue-300"
                              : "cursor-pointer bg-blue-600 hover:bg-blue-700"
                          }`}
                        >
                          <Download className="w-4 h-4" />
                          {selectedIsInFlight
                            ? selectedFiles.size === 1
                              ? "Downloading…"
                              : "Already preparing…"
                            : `Download (${selectedFiles.size})`}
                        </button>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {captableStatsPopup && (
        <Transition.Root show={!!captableStatsPopup} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-20"
            onClose={() => setCaptableStatsPopup(null)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-60 transition-opacity" />
            </Transition.Child>
            <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md rounded-lg bg-white shadow-xl flex flex-col max-h-[85vh]">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
                    <Dialog.Title className="text-sm font-semibold text-gray-900">
                      Extraction Stats
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={() => setCaptableStatsPopup(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="px-4 py-3 space-y-3 text-xs text-gray-700 overflow-y-auto">
                    {captableStatsPopup.mgt7 && (
                      <div>
                        <div className="font-medium text-gray-900 mb-1">
                          MGT-7
                        </div>
                        <div className="bg-gray-50 rounded p-2 space-y-1">
                          <div className="truncate text-gray-500">
                            {captableStatsPopup.mgt7.file}
                          </div>
                          <div className="flex gap-4">
                            <span>
                              <span className="font-medium">
                                {captableStatsPopup.mgt7.shareholders}
                              </span>{" "}
                              shareholders
                            </span>
                            <span>
                              Individual list:{" "}
                              <span className="font-medium">
                                {captableStatsPopup.mgt7.providesIndividualList
                                  ? "Yes"
                                  : "No"}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    {captableStatsPopup.pas3s.length > 0 && (
                      <div>
                        <div className="font-medium text-gray-900 mb-1">
                          PAS-3 ({captableStatsPopup.pas3s.length})
                        </div>
                        <div className="space-y-2">
                          {captableStatsPopup.pas3s.map((p, i) => (
                            <div
                              key={i}
                              className="bg-gray-50 rounded p-2 space-y-1"
                            >
                              <div className="truncate text-gray-500">
                                {p.file}
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                                <span>
                                  <span className="font-medium">
                                    {p.rounds}
                                  </span>{" "}
                                  round{p.rounds !== 1 ? "s" : ""}
                                </span>
                                <span>
                                  <span className="font-medium">
                                    {p.allottees}
                                  </span>{" "}
                                  allottees
                                </span>
                                {p.placeholders > 0 && (
                                  <span className="text-amber-600">
                                    <span className="font-medium">
                                      {p.placeholders}
                                    </span>{" "}
                                    placeholder
                                    {p.placeholders !== 1 ? "s" : ""}
                                  </span>
                                )}
                                <span>
                                  <span className="font-medium">{p.loas}</span>{" "}
                                  LOA{p.loas !== 1 ? "s" : ""}
                                </span>
                              </div>
                              {p.loaIssues.length > 0 && (
                                <ul className="mt-1 space-y-0.5">
                                  {p.loaIssues.map((issue, j) => (
                                    <li
                                      key={j}
                                      className="text-amber-700 bg-amber-50 rounded px-2 py-0.5"
                                    >
                                      {issue}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-6 pt-1 border-t border-gray-100">
                      <span>
                        Stage 3 rows:{" "}
                        <span className="font-medium">
                          {captableStatsPopup.stage3Rows}
                        </span>
                      </span>
                      <span>
                        Allotment rows:{" "}
                        <span className="font-medium">
                          {captableStatsPopup.allotmentRows}
                        </span>
                      </span>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>
      )}

      {pdfViewer && (
        <PdfViewerModal
          url={pdfViewer.url}
          base64Data={pdfViewer.base64Data}
          title={pdfViewer.fileName}
          hasPrevious={pdfViewerHistory.length > 0}
          navigatingBack={navigatingBack}
          onPrevious={handleGoBackPdf}
          onClose={() => {
            setPdfViewer(null);
            setPdfViewerHistory([]);
          }}
        />
      )}
    </>
  );
};

function renderTreeNodes(
  tree: FileTree,
  depth: number,
  expanded: Set<string>,
  toggleFolder: (path: string) => void,
  selectedFiles: Set<string>,
  toggleFile: (file: string) => void,
  toggleFolderSelection: (
    folderTree: FileTree,
    folderPath: string,
    select: boolean,
  ) => void,
  handleDownloadOne: (file: string) => void,
  isFileInFlight: (file: string) => boolean,
  fileDates: Map<string, ExtractedDate | null>,
  latestByType: Map<FileTypeKey, string>,
  dupMarks: Map<string, DupAnnotation>,
  pas3StatusByPath: Map<string, Pas3RunFileStatus>,
  onViewFile: (path: string) => void,
  onShowAttachments: (path: string) => void,
  viewingFiles: Set<string>,
): JSX.Element[] {
  const indent = { paddingLeft: `${depth * 16 + 8}px` };
  return Object.entries(tree).flatMap(([key, value]) => {
    const path = key;
    const displayName = key.split("/").filter(Boolean).pop() ?? key;
    if (value === "file") {
      const checked = selectedFiles.has(path);
      const fileType = classifyFile(path);
      const extracted = fileDates.get(path) ?? null;
      const isLatest = !!fileType && latestByType.get(fileType) === path;
      const dupMark = dupMarks.get(path);
      const pas3Status =
        fileType === "PAS-3" ? pas3StatusByPath.get(path) : null;
      const isPdf = path.toLowerCase().endsWith(".pdf");
      return [
        <div
          key={`tree-file:${path}`}
          style={indent}
          className={`flex items-center gap-2 pr-3 py-1.5 cursor-pointer ${
            checked ? "bg-blue-50" : "hover:bg-gray-50"
          }`}
          onClick={() => toggleFile(path)}
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={() => toggleFile(path)}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-400"
          />
          <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <span className="flex-1 truncate text-gray-700" title={path}>
            {displayName}
          </span>
          {isLatest && (
            <span className="px-2 py-0.5 text-[10px] rounded bg-green-100 text-green-700 border border-green-200">
              Latest
            </span>
          )}
          {dupMark && (
            <span
              className={`px-2 py-0.5 text-[10px] rounded border ${
                dupMark.tone === "info"
                  ? "bg-blue-50 text-blue-700 border-blue-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
              }`}
            >
              {dupMark.label}
            </span>
          )}
          {pas3Status && renderPas3StatusBadge(pas3Status)}
          <span
            className={`text-[11px] tabular-nums w-24 text-right ${
              extracted ? "text-gray-600" : "text-gray-300"
            }`}
            title={
              extracted
                ? `confidence: ${extracted.confidence}`
                : "no date detected"
            }
          >
            {formatExtractedDate(extracted)}
          </span>
          {fileType && (
            <span className="px-2 py-0.5 text-[10px] rounded bg-gray-100 text-gray-600 border border-gray-200">
              {fileType}
            </span>
          )}
          {isPdf && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewFile(path);
              }}
              disabled={viewingFiles.has(path)}
              className={`p-1 rounded ${
                viewingFiles.has(path)
                  ? "cursor-not-allowed opacity-40"
                  : "hover:bg-blue-100"
              }`}
              title="View"
            >
              {viewingFiles.has(path) ? (
                <Loader2 className="w-4 h-4 text-blue-700 animate-spin" />
              ) : (
                <Eye className="w-4 h-4 text-blue-700" />
              )}
            </button>
          )}
          {isPdf && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onShowAttachments(path);
              }}
              className="p-1 rounded hover:bg-purple-100"
              title="Show attachments"
            >
              <Paperclip className="w-4 h-4 text-purple-600" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadOne(path);
            }}
            disabled={isFileInFlight(path)}
            className={`p-1 rounded ${
              isFileInFlight(path)
                ? "cursor-not-allowed opacity-40"
                : "hover:bg-blue-100"
            }`}
            title={isFileInFlight(path) ? "Downloading" : "Download"}
          >
            <Download className="w-4 h-4 text-blue-700" />
          </button>
        </div>,
      ];
    }
    if (typeof value === "object") {
      const isOpen = expanded.has(path);
      const descendants = collectAllFiles(value);
      const allSelected =
        descendants.length > 0 &&
        descendants.every((f) => selectedFiles.has(f));
      const someSelected = descendants.some((f) => selectedFiles.has(f));
      return [
        <div
          key={`tree-folder:${path}`}
          style={indent}
          className="flex items-center gap-2 pr-3 py-1.5 hover:bg-gray-50 cursor-pointer text-gray-800"
          onClick={() => toggleFolder(path)}
        >
          {isOpen ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = !allSelected && someSelected;
            }}
            onChange={() => toggleFolderSelection(value, path, !allSelected)}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-400"
          />
          <Folder className="w-4 h-4 text-yellow-500 flex-shrink-0" />
          <span className="font-medium text-sm truncate" title={path}>
            {displayName}
          </span>
          <span className="ml-1 text-[10px] text-gray-400">
            {descendants.length}
          </span>
        </div>,
        ...(isOpen
          ? renderTreeNodes(
              value,
              depth + 1,
              expanded,
              toggleFolder,
              selectedFiles,
              toggleFile,
              toggleFolderSelection,
              handleDownloadOne,
              isFileInFlight,
              fileDates,
              latestByType,
              dupMarks,
              pas3StatusByPath,
              onViewFile,
              onShowAttachments,
              viewingFiles,
            )
          : []),
      ];
    }
    return [];
  });
}

function renderFlatList(
  files: string[],
  selectedFiles: Set<string>,
  toggleFile: (file: string) => void,
  handleDownloadOne: (file: string) => void,
  isFileInFlight: (file: string) => boolean,
  fileDates: Map<string, ExtractedDate | null>,
  latestByType: Map<FileTypeKey, string>,
  dupMarks: Map<string, DupAnnotation>,
  pas3StatusByPath: Map<string, Pas3RunFileStatus>,
  onViewFile: (path: string) => void,
  onShowAttachments: (path: string) => void,
  viewingFiles: Set<string>,
): JSX.Element[] {
  return files.map((path) => {
    const checked = selectedFiles.has(path);
    const displayName = path.split("/").filter(Boolean).pop() ?? path;
    const folder = path.split("/").slice(0, -1).join("/");
    const fileType = classifyFile(path);
    const extracted = fileDates.get(path) ?? null;
    const isLatest = !!fileType && latestByType.get(fileType) === path;
    const dupMark = dupMarks.get(path);
    const pas3Status = fileType === "PAS-3" ? pas3StatusByPath.get(path) : null;
    const isPdf = path.toLowerCase().endsWith(".pdf");
    return (
      <div
        key={`flat-file:${path}`}
        className={`flex items-center gap-2 px-2 pr-3 py-1.5 cursor-pointer ${
          checked ? "bg-blue-50" : "hover:bg-gray-50"
        }`}
        onClick={() => toggleFile(path)}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={() => toggleFile(path)}
          onClick={(e) => e.stopPropagation()}
          className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-400"
        />
        <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="truncate text-gray-700" title={path}>
            {displayName}
          </div>
          {folder && (
            <div className="truncate text-[10px] text-gray-400" title={folder}>
              {folder}
            </div>
          )}
        </div>
        {isLatest && (
          <span className="px-2 py-0.5 text-[10px] rounded bg-green-100 text-green-700 border border-green-200">
            Latest
          </span>
        )}
        {dupMark && (
          <span
            className={`px-2 py-0.5 text-[10px] rounded border ${
              dupMark.tone === "info"
                ? "bg-blue-50 text-blue-700 border-blue-200"
                : "bg-amber-50 text-amber-700 border-amber-200"
            }`}
          >
            {dupMark.label}
          </span>
        )}
        {pas3Status && renderPas3StatusBadge(pas3Status)}
        <span
          className={`text-[11px] tabular-nums w-24 text-right ${
            extracted ? "text-gray-600" : "text-gray-300"
          }`}
          title={
            extracted
              ? `confidence: ${extracted.confidence}`
              : "no date detected"
          }
        >
          {formatExtractedDate(extracted)}
        </span>
        {fileType && (
          <span className="px-2 py-0.5 text-[10px] rounded bg-gray-100 text-gray-600 border border-gray-200">
            {fileType}
          </span>
        )}
        {isPdf && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewFile(path);
            }}
            disabled={viewingFiles.has(path)}
            className={`p-1 rounded ${
              viewingFiles.has(path)
                ? "cursor-not-allowed opacity-40"
                : "hover:bg-blue-100"
            }`}
            title="View"
          >
            {viewingFiles.has(path) ? (
              <Loader2 className="w-4 h-4 text-blue-700 animate-spin" />
            ) : (
              <Eye className="w-4 h-4 text-blue-700" />
            )}
          </button>
        )}
        {isPdf && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onShowAttachments(path);
            }}
            className="p-1 rounded hover:bg-purple-100"
            title="Show attachments"
          >
            <Paperclip className="w-4 h-4 text-purple-600" />
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDownloadOne(path);
          }}
          disabled={isFileInFlight(path)}
          className={`p-1 rounded ${
            isFileInFlight(path)
              ? "cursor-not-allowed opacity-40"
              : "hover:bg-blue-100"
          }`}
          title={isFileInFlight(path) ? "Downloading" : "Download"}
        >
          <Download className="w-4 h-4 text-blue-700" />
        </button>
      </div>
    );
  });
}
